// ** React Imports
import React, { useEffect, useState } from "react";

// ** React Icon Imports
import { BsClipboard2Data, BsPerson } from "react-icons/bs";
import { SiAwsorganizations } from "react-icons/si";
import { GoProject } from "react-icons/go";
import { GoBell } from "react-icons/go";

import { useNavigate } from "react-router-dom";

// ** Custom Imports
import CustomSkeleton from "../components/CustomSkeleton";
import BreadCrumb from "../components/BreadCrumb";
import Box from "../components/ui/Box";
import { Header } from "./Home";
import CustomButton from "../components/CustomButton";
import Pagination from "../components/ui/Pagination";

// ** context imports
import { useNotification } from "../contexts/NotificationContextProvider";
import { updateCurrentOrganization } from "../api/Api";
import { showToast } from "../utils/toastUtils";

// ** Icon list
const icons = [
  {
    name: "FOLLOW",
    button_name: "View Profile",
    icon: <BsPerson size={25} className={"text-white"} />,
  },
  {
    name: "PROJECT_INVITED",
    button_name: "View Project",
    icon: <GoProject size={25} className={"text-white"} />,
  },
  {
    name: "PROJECT_JOINED",
    button_name: "View Project",
    icon: <GoProject size={25} className={"text-white"} />,
  },
  {
    name: "REPORT_REVIEW",
    button_name: "View Report",
    icon: <BsClipboard2Data size={25} className={"text-white"} />,
  },
  {
    name: "ORGANIZATION_JOINED",
    button_name: "View Organization",
    icon: <SiAwsorganizations size={25} className={"text-white"} />,
  },
];

// main component for notifications
const Notifications = () => {
  const {
    notifications,
    loading,
    markNotificationsAsRead,
    markAllNotificationsAsRead,
    pagination,
    setPagination,
  } = useNotification();

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1, // Reset to first page when component mounts
    }));
  }, []);

  return (
    <Box>
      <BreadCrumb
        array={[{ title: "Notifications" }]}
        icon={<GoBell size={22} color="#0bd5fe" />}
      />
      <div className="bg-white">
        <Box innerClass="primary-box">
          <Header title="All Notifications" showButton={false} />
          <Box>
            <div className="d-flex flex-column gap-2">
              <div className="w-100 d-flex justify-content-end my-1">
                <CustomButton
                  variant="outline"
                  className=" p4 mb-2"
                  onClick={markAllNotificationsAsRead}
                >
                  Mark All As Read
                </CustomButton>
              </div>

              {!loading ? (
                notifications?.length > 0 ? (
                  <>
                    {notifications.map((notification) => (
                      <NotificationCard
                        item={notification}
                        key={notification.id}
                        markNotificationsAsRead={markNotificationsAsRead}
                      />
                    ))}
                  </>
                ) : (
                  <div className="text-center text-muted p4 py-5">
                    <p>No notifications yet. You're all caught up!</p>
                  </div>
                )
              ) : (
                <CustomSkeleton
                  height={90}
                  count={10}
                  style={{ marginBottom: "10px" }}
                />
              )}
            </div>
          </Box>

          {/* Pagination Component */}
          {!loading && notifications.length > 0 && (
            <Pagination
              currentPage={pagination.currentPage}
              totalItems={pagination.totalItems}
              onPageChange={(page) => {
                setPagination((prev) => ({
                  ...prev,
                  currentPage: page,
                }));
              }}
              pageSize={pagination.pageSize}
            />
          )}
        </Box>
      </div>
    </Box>
  );
};

export default Notifications;

// ** time duration function to calculate the time difference
const timeDuration = (date) => {
  const now = new Date();
  const createdAtDate = new Date(date);

  const diffInMs = now - createdAtDate;
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInDays > 0) {
    return createdAtDate.toISOString().split("T")[0];
  } else if (diffInHours > 0) {
    return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
  } else {
    return `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;
  }
};

//---------------------------------------------------------------------
// ** Notification Card Component
// ** Notification Card Component
const NotificationCard = ({ item, markNotificationsAsRead }) => {
  const [notificationType] = useState(
    () => icons.find((icon) => icon.name === item.type) || null
  );
  const navigate = useNavigate();

//  Function to handle button click based on notification type 
const handleButtonClick = async () => {
  if (!item?.redirect_url) {
    console.warn("Redirect URL is missing.");
    showToast("Redirect URL is missing.", "error");
    return;
  }
  if (item.type === "ORGANIZATION_JOINED") {
    const match = item.redirect_url.match(/\/organization\/(\d+)/);
    const orgId = match ? parseInt(match[1]) : null;

    if (orgId) {
      try {
        await updateCurrentOrganization(orgId);
        navigate("/home"); // Redirect to home after switching organization
        showToast("Organization Changed", "success");
      } catch (err) {
        console.error("Failed to switch organization", err);
        showToast("Failed to switch organization", "error");
      }
    }
  } else {
    navigate(item.redirect_url);
  }
};


  return (
    <div
      className="d-flex p-3 column-gap-2 w-100 rounded-4 secondary-box"
      style={{ backgroundColor: item.is_read ? "#FFF" : "#F5F5F5" }}
      onClick={() => {
        if (!item.is_read) {
          markNotificationsAsRead(item.id);
        }
      }}
    >
      {/* dot icon */}
      <div className="px-2 d-flex" style={{ height: "max-content" }}>
        <div
          className="rounded-5 d-flex justify-content-center align-items-center"
          style={{ width: "50px", height: "50px", backgroundColor: "#333" }}
        >
          {notificationType?.icon}
        </div>
      </div>

      {/* notification message */}
      <div className="d-flex flex-column w-100 row-gap-2">
        <div className="d-flex w-100 justify-content-between">
          <span className="p4 fw-semibold">{item.message}.</span>
          <div
            className={`d-flex border rounded-circle ${
              item.is_read ? "d-none" : ""
            }`}
            style={{
              width: "max-content",
              height: "max-content",
              padding: "1px",
              borderColor: "#C4C4C4",
            }}
          >
            <span
              className={"rounded-circle d-block"}
              style={{
                width: "8px",
                height: "8px",
                backgroundColor: "#90DFEC",
              }}
            ></span>
          </div>
        </div>

        {/* view report button */}
        <div className="d-flex w-100 justify-content-between align-items-end">
          <CustomButton
            className="py-2 p4"
            onClick={handleButtonClick}
          >
            {notificationType?.button_name}
          </CustomButton>
          <span className="text-secondary p5">
            {timeDuration(item.created_at)}
          </span>
        </div>
      </div>
    </div>
  );
};
